import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import NavBar from '../components/NavBar.jsx';
import FooterContainer from '../components/FooterContainer.jsx';
import RecognizeAchievements from '../components/RecognizeAchievements.jsx';
import Faqs from '../components/home/Faqs.jsx';
import Purpose from '../components/home/Purpose.jsx';
import CompaniesList from '../components/home/CompaniesList.jsx';
import LatestBlogArticles from '../components/LatestBlogArticles.jsx';
import PlatformsIn from '../components/home/PlataformsIn.jsx';
import TemplateGallery from '../components/home/TemplateGallery.jsx';
import FeaturesGrid from '../components/home/FeaturesGrid.jsx';
import ExampleDialog from '../components/dialogs/ExampleDialog.jsx';
import { HeroSection } from '../components/sections/HeroSection.jsx';
import { HeroBadge } from '../components/sections/HeroBadge.jsx';
import { StatsBar } from '../components/sections/StatsBar.jsx';
import InstitutionCards from '../components/home/InstitutionCards.jsx';
import PlatformSection from '../components/home/PlatformSection.jsx';
import PlatformScale from '../components/home/PlatformScale.jsx';
import WhiteLabel from '../components/home/WhiteLabel.jsx';
import StandardsCards from '../components/home/StandardsCards.jsx';
import { ProductFamilySection } from '../components/products/ProductFamilySection.jsx';
import { InternalLinksSection } from '../components/sections/InternalLinksSection.jsx';
import { CtaButton } from '../components/sections/CtaButton.jsx';
import { Breadcrumbs } from '../components/sections/Breadcrumbs.jsx';
import { ArrowIcon } from '../components/sections/ArrowIcon.jsx';
import { Icon } from '../components/sections/Icon.jsx';
import { ACCENT_GRAD, BLUE, CYAN, FG_2, FG_3, INK, LINE, PURPLE, RADII, YELLOW } from '../components/sections/theme.js';
import { useLocalizedPath } from '../hooks/useLocalizedPath.jsx';
import { POK_FACTS, POK_INSTITUTIONS_LABEL, POK_NAME, SIGNUP_URL } from '../constants.js';
import { detectLang, initI18n } from '../i18n.js';
import { getLatestPosts } from '../blog/manifest-latest.js';
import { generateBlogJsonLd } from '../utils/api.js';
import { localizedPath } from '../utils/languageUtils.js';
import { buildBreadcrumbSchema, buildFaqSchema, pokPageMeta } from '../utils/pageJsonLd.js';
import { siteOrigin } from '../utils/siteUrl.js';
import { POK_SOFTWARE, POK_WEBSITE } from '../utils/pokJsonLd.js';
import { TrackedEvent } from '../utils/analytics.js';

const combinedJsonLD = {
    '@context': 'https://schema.org',
    '@graph': [
        POK_SOFTWARE,
        {
            ...POK_WEBSITE,
            mentions: [
                {
                    '@type': 'Thing',
                    name: 'Open Badges 3.0',
                    url: 'https://www.imsglobal.org/spec/ob/v3p0/',
                },
                {
                    '@type': 'Thing',
                    name: 'W3C Verifiable Credentials',
                    url: 'https://www.w3.org/TR/vc-data-model/',
                },
                {
                    '@type': 'Thing',
                    name: 'European Learning Model (ELM)',
                    url: 'https://europass.europa.eu/en/european-digital-credentials',
                },
                {
                    '@type': 'Thing',
                    name: 'IMS Global / 1EdTech',
                    url: 'https://www.imsglobal.org/',
                },
            ],
        },
    ],
};

const FAQS = [1, 2, 41, 3, 5, 8, 22, 35, 37, 38, 39];

export function meta({ location }) {
    const lang = detectLang(location.pathname);
    const t = initI18n(location).getFixedT(lang);

    return pokPageMeta([
        { 'script:ld+json': combinedJsonLD },
        {
            'script:ld+json': buildBreadcrumbSchema(
                [{ name: POK_NAME, path: localizedPath('home', lang) }],
                siteOrigin(),
            ),
        },
        { 'script:ld+json': buildFaqSchema(FAQS, t) },
        { 'script:ld+json': generateBlogJsonLd(lang, getLatestPosts(lang)) },
    ]);
}

const HOME_STATS = [
    { value: '1.5', suffix: 'M+', dot: '#58E07E', tKey: 'home.stats.1' },
    { prefix: '+', value: POK_INSTITUTIONS_LABEL, dot: CYAN, tKey: 'home.stats.2' },
    { value: String(POK_FACTS.countries), dot: BLUE, tKey: 'home.stats.3' },
    { value: '100', suffix: '%', dot: YELLOW, tKey: 'home.stats.4' },
];

export default function Home() {
    const { t } = useTranslation();
    const { dc, mc, ob, integrations, prices, home } = useLocalizedPath();
    const [showExample, setShowExample] = useState(false);

    const stats = HOME_STATS.map(({ tKey, ...rest }) => ({ ...rest, label: t(tKey) }));

    const relatedLinks = [
        { label: t('prices.related.dc'), href: dc() },
        { label: t('prices.related.mc'), href: mc() },
        { label: t('prices.related.ob'), href: ob() },
        { label: t('prices.related.integrations'), href: integrations() },
    ];

    return (
        <>
            <NavBar>
                <Breadcrumbs items={[{ name: POK_NAME, path: home() }]} />

                {/* ── HERO ── */}
                <HeroSection>
                    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                        <Grid
                            container
                            spacing={6}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Grid size={{ xs: 12, md: 6 }}>
                                <HeroBadge label={t('home.badge')} />
                                <Typography
                                    variant="h1"
                                    component="h1"
                                    sx={{
                                        fontSize: { xs: '2rem', md: '3.6rem' },
                                        lineHeight: { xs: 1.1, md: 1.0 },
                                        letterSpacing: '-0.04em',
                                        mb: 2.5,
                                        fontWeight: 700,
                                    }}
                                >
                                    <Trans
                                        i18nKey="home.hero_title"
                                        components={{
                                            em: (
                                                <Box
                                                    component="em"
                                                    sx={{
                                                        fontStyle: 'normal',
                                                        color: PURPLE,
                                                    }}
                                                />
                                            ),
                                        }}
                                    />
                                </Typography>
                                <Typography
                                    variant="var500x18"
                                    sx={{
                                        color: FG_2,
                                        mb: 4,
                                        fontSize: '1.1875rem',
                                        fontWeight: 400,
                                        lineHeight: 1.55,
                                        maxWidth: 540,
                                    }}
                                    component="p"
                                >
                                    {t('home.hero_desc')}
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.75}
                                    sx={{
                                        mb: 2.25,
                                    }}
                                >
                                    <CtaButton href={SIGNUP_URL} data-track={TrackedEvent.START_REGISTRATION}>
                                        {t('home.hero_cta')}
                                    </CtaButton>
                                    <Button
                                        variant="outlined"
                                        component="a"
                                        href={prices()}
                                        data-track={TrackedEvent.SCHEDULE_DEMO}
                                        sx={{
                                            color: PURPLE,
                                            borderColor: 'rgba(81,36,166,0.3)',
                                            px: 3,
                                            py: 1.5,
                                            borderRadius: RADII.sm,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            '&:hover': {
                                                background: 'rgba(81,36,166,0.06)',
                                                borderColor: PURPLE,
                                            },
                                        }}
                                    >
                                        {t('commons.button.schedule_demo')}
                                    </Button>
                                </Stack>
                                <Stack
                                    spacing={1.25}
                                    sx={{
                                        alignItems: { xs: 'center', md: 'flex-start' },
                                        mb: 2,
                                    }}
                                >
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => setShowExample(true)}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            m: 0,
                                            background: '#fff',
                                            border: `1px solid ${LINE}`,
                                            borderRadius: RADII.md,
                                            p: '8px 16px 8px 8px',
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            textAlign: 'left',
                                            boxShadow: '0 10px 26px -16px rgba(20,11,61,0.3)',
                                            transition: 'transform .18s ease, box-shadow .18s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 22px 46px -22px rgba(20,11,61,0.28)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                flexShrink: 0,
                                                borderRadius: RADII.sm,
                                                background: ACCENT_GRAD,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Icon name="badge" size={22} color="#fff" />
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 700,
                                                    color: INK,
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                {t('home.hero_secondary')}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={{ fontSize: '0.75rem', color: FG_3, lineHeight: 1.25 }}
                                            >
                                                {t('home.hero_secondary_sub')}
                                            </Typography>
                                        </Box>
                                        <ArrowIcon size={20} sx={{ color: PURPLE, ml: 0.5 }} />
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: FG_3,
                                            fontSize: '0.8125rem',
                                            textAlign: { xs: 'center', md: 'left' },
                                        }}
                                    >
                                        {t('home.hero_trust')}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: 'center' }}>
                                <Box
                                    component="video"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                    poster="/images/home/hero-anim-poster.webp"
                                    aria-label={t('commons.main_description.hero_alt')}
                                    sx={{
                                        width: '100%',
                                        maxWidth: 560,
                                        height: 'auto',
                                        borderRadius: '18px',
                                        boxShadow: '0 24px 60px -30px rgba(81,36,166,0.30)',
                                    }}
                                >
                                    <source src="/images/home/hero-anim.mp4" type="video/mp4" />
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </HeroSection>

                {/* ── STATS ── */}
                <StatsBar stats={stats} />

                {/* ── SOCIAL PROOF: COMPANIES ── */}
                <Box component="section" id="instituciones" sx={{ background: '#fff' }}>
                    <CompaniesList />
                </Box>

                {/* ── PRODUCTS ── */}
                <ProductFamilySection background="#f8f4ff" />

                {/* ── USE CASES ── */}
                <InstitutionCards />

                {/* ── COMPLETE PLATFORM (animated steps) ── */}
                <PlatformSection />

                {/* ── ONE PLATFORM AT ANY SCALE (bento) ── */}
                <PlatformScale />

                {/* ── WHITE-LABEL (compare slider) ── */}
                <WhiteLabel />

                {/* ── STANDARDS ── */}
                <StandardsCards />

                {/* ── WHY POK / COMPARISON ── */}
                <Purpose />

                {/* ── PLATFORM RATINGS ── */}
                <PlatformsIn />

                {/* ── TEMPLATE LIBRARY ── */}
                <TemplateGallery />

                {/* ── 9 FEATURES ── */}
                <FeaturesGrid />

                {/* ── FAQ ── */}
                <Faqs questions={FAQS} seeMore={true} />

                {/* ── FINAL CTA ── */}
                <RecognizeAchievements />

                {/* ── BLOG ── */}
                <LatestBlogArticles />

                <InternalLinksSection title={t('prices.related.title')} links={relatedLinks} />

                <FooterContainer />
            </NavBar>
            <ExampleDialog onClose={() => setShowExample(false)} open={showExample} />
        </>
    );
}
